import dayjs from "dayjs";
import { Injectable, inject } from "@angular/core";
import { ApiResource } from "@auvious/common";
import { AuviousRtcService } from "./rtc.service";
import {
  AppointmentEventType,
  AppointmentParticipantStateEnum,
  AppointmentViewState,
  IAppointmentContact,
  IAppointmentNotificationLog,
  IAppointmentNotificationLogEvent,
  IAppointmentParticipantBase,
  IAppointmentRouting,
  IContactBase,
} from "../models/Appointment";
import {
  PageableRequest,
  IPagedResponse,
  AppointmentLogRequestBuilder,
  AppointmentLogEventRequestBuilder,
} from "../models/Pageable";
import { Subject, BehaviorSubject, merge, filter } from "rxjs";
import { IAppointmentLocale } from "../models/Appointment";
import {
  IAppointmentNotification,
  IAppointmentFormat,
} from "../models/Appointment";
import {
  IAppointment,
  IAppointmentTimeFrameCount,
  IScheduleAppointmentRequest,
  IScheduleAppointmentOptions,
  IScheduledAppointmentContact,
} from "../models/Appointment";
import { GenericErrorHandler } from "./error-handlers.service";
import { ApplicationService } from "./application.service";
import {
  IWebhookSubscription,
  IWebhookSubscriptionBasicAuth,
  IWebhookSubscriptionClientCredentials,
} from "../models/Webhooks";
import {
  AppointmentNotificationTypeEnum,
  AppointmentWebhookNotificationTypeEnum,
  ConversationOriginEnum,
  ScheduleTypeEnum,
  TerminateReasonEnum,
  WebhookSubscriptionAuthenticationEnum,
} from "../core-ui.enums";
import { debug, debugError, stripOrganizationIdPrefix } from "./utils";
import { UserService } from "./user.service";
import { IntegrationService } from "../../app/services/integration.service";
import { InteractionService } from "../../app/services/interaction.service";
import { AppConfigService } from "./app.config.service";
import { AgentParam, IInteraction } from "../models";
import { IConferenceSession } from "@auvious/rtc";
import { Router } from "@angular/router";
import { NotificationService } from "./notification.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class AppointmentService {
  private rtcService = inject(AuviousRtcService);
  private errorHandler = inject(GenericErrorHandler);
  private application = inject(ApplicationService);
  private interaction = inject(InteractionService);
  private integration = inject(IntegrationService);
  private user = inject(UserService);
  private config = inject(AppConfigService);
  private router = inject(Router);
  private notification = inject(NotificationService);
  private translate = inject(TranslateService);

  private tagFormats: IAppointmentFormat[] = [];
  private supportedLocales: IAppointmentLocale[] = [];
  private webhookTypes: AppointmentWebhookNotificationTypeEnum[] = [];

  private appointmentResource: ApiResource;
  private appointmentCountResource: ApiResource;
  private notificationResource: ApiResource;

  // this should be behavioursubject because as a customer,
  // it is being set by the guard and then read by the appointment page
  // this is also used in CRUD operations while scheduling
  private _appointmentChanged = new BehaviorSubject<
    IAppointment<IAppointmentRouting>
  >(undefined);
  public appointmentChanged$ = this._appointmentChanged.asObservable();

  private _appointmentCreated = new Subject<string>();
  public appointmentCreated$ = this._appointmentCreated.asObservable();

  private _appointmentStateChanged = new Subject<{
    appointmentId: string;
    type: AppointmentEventType;
  }>();
  public appointmentStateChanged$ =
    this._appointmentStateChanged.asObservable();

  private _appointmentEnded = new Subject<IAppointment<IAppointmentRouting>>();
  public appointmentEnded$ = this._appointmentEnded.asObservable();

  private _appointmentParticipantStateChanged = new Subject<{
    appointmentId: string;
    type: AppointmentEventType;
  }>();
  public appointmentParticipantStateChanged$ =
    this._appointmentParticipantStateChanged.asObservable();

  private _appointmentContacts = new BehaviorSubject<
    Map<string, IScheduledAppointmentContact>
  >(new Map());
  appointmentContacts$ = this._appointmentContacts.asObservable();

  private _appointmentContactsMap: Map<string, IScheduledAppointmentContact> =
    new Map();

  private isAlreadyListening = false;
  private endingInteraction: IInteraction;
  private completedAppointmentInteractions: {
    [interactionId: string]: boolean;
  } = {};

  constructor() {
    this.rtcService.common$.subscribe((common) => {
      this.appointmentResource = common.apiResourceFactory(
        "api/v2/appointments"
      );
      // the appointment count endpoints only exist on v1
      this.appointmentCountResource = common.apiResourceFactory("api/appointments");
      this.notificationResource =
        common.apiResourceFactory("api/notifications");
    });
  }

  public setAppointmentAsCompletedInInteraction(interactionId: string) {
    this.completedAppointmentInteractions = {
      ...this.completedAppointmentInteractions,
      [interactionId]: true,
    };
  }

  /**
   * caches an appointment participant locally
   * @param participant
   */
  public registerAppointmentParticipant(
    participant: IScheduledAppointmentContact
  ) {
    this._appointmentContactsMap.set(participant.id, participant);
    this._appointmentContacts.next(this._appointmentContactsMap);
  }

  /**
   * caches appointment participants locally. If not found locally, they are retrieved from the service
   * @param appointmentId
   * @param participants
   */
  public async requestAppointmentParticipants(
    appointmentId: string,
    participants: { id: string; detailsAvailable?: boolean }[]
  ) {
    participants.forEach(async (p) => {
      // the backend tells us up front whose details it can resolve (agent
      // participants carry none), so asking for them only yields a failed request
      if (p.detailsAvailable === false) return;
      if (this._appointmentContactsMap.has(p.id)) return;

      this._appointmentContactsMap.set(p.id, undefined);

      try {
        const contact = await this.getParticipantDetails(appointmentId, p.id);
        this._appointmentContactsMap.set(p.id, contact);
      } catch (ex) {
        // drop the placeholder, otherwise the failed lookup is cached for good
        this._appointmentContactsMap.delete(p.id);
        debugError(ex);
      }

      this._appointmentContacts.next(this._appointmentContactsMap);
    });

    // const results = await Promise.all(
    //   Array.from(this.organizerMap.entries())
    //     .filter(([id, contact]) => !contact)
    //     .map(async ([id]) => {
    //       try {
    //         const a = this.organizerAppointmentMap.get(id);
    //         const contact = await this.appointmentService.getParticipantDetails(
    //           a,
    //           id
    //         );
    //         return { id, contact };
    //       } catch (ex) {
    //         return { id, contact: undefined };
    //       }
    //     })
    // );
    // results.forEach((r) => this.organizerMap.set(r.id, r.contact));
    // this._organizers.next(this.organizerMap);
    // }
  }

  /**
   * Refreshes cached participant data for a given appointment.
   * Called when an APUpdatedEvent is received so stale contact details are re-fetched.
   */
  private async refreshCachedParticipants(appointmentId: string) {
    try {
      const appointment = await this.get(appointmentId);
      // only customer details can go stale through an appointment update, and
      // only they can be resolved at all: the organizer and agent participants
      // carry no details, so asking for them only yields a failed request
      const participants = (appointment.participants || []).filter(
        (p) =>
          p &&
          p.type === "CUSTOMER" &&
          p.detailsAvailable !== false &&
          this._appointmentContactsMap.has(p.id)
      );

      if (participants.length === 0) return;

      // replace each entry in place rather than evicting it first, so the
      // already-resolved contact stays on screen while its details are refetched
      await Promise.all(
        participants.map(async (p) => {
          try {
            const contact = await this.getParticipantDetails(
              appointmentId,
              p.id
            );
            this._appointmentContactsMap.set(p.id, contact);
          } catch (ex) {
            // keep the details we already have
            debugError(ex);
          }
        })
      );

      this._appointmentContacts.next(this._appointmentContactsMap);
    } catch (ex) {
      debugError(ex);
    }
  }

  /**
   * Updates the cached participants' tickets from an updated appointment.
   * On reschedule the backend re-issues each participant's tickets (room access /
   * manage links) and invalidates the previous ones, so the cached contacts hold
   * stale ticket urls. We merge the fresh tickets from the updated appointment into
   * the already-cached contacts, preserving their resolved details (name, contact,
   * metadata) which the appointment participant objects do not carry.
   */
  public updateCachedParticipantTickets(
    appointment: IAppointment<IAppointmentRouting>
  ) {
    let changed = false;

    [appointment.organizer, ...(appointment.participants || [])]
      .filter((p) => p && this._appointmentContactsMap.get(p.id))
      .forEach((p) => {
        const cached = this._appointmentContactsMap.get(p.id);
        this._appointmentContactsMap.set(p.id, {
          ...cached,
          ticket: p.ticket,
          tickets: p.tickets,
        });
        changed = true;
      });

    if (changed) {
      this._appointmentContacts.next(this._appointmentContactsMap);
    }
  }

  /**
   * Clears all locally cached appointment participants and emits the empty map.
   * Used when the appointments list is manually refreshed so stale contact
   * details (name, contact, tickets) are re-fetched from the service on the
   * next requestAppointmentParticipants() call.
   */
  public clearCachedParticipants() {
    this._appointmentContactsMap.clear();
    this._appointmentContacts.next(this._appointmentContactsMap);
  }

  public listenForEndedInteractions() {
    // don't even bother if no appointments or interactions supported
    if (!this.isAppointmentInteractionAvailable || this.isAlreadyListening)
      return;

    this.isAlreadyListening = true;

    // in genesys we get the ending/ended events
    // in talkdesk we only get the ended event
    merge(
      this.interaction.eventClient?.ending$,
      this.interaction.eventClient?.ended$
    )
      .pipe(
        filter((id) => {
          const inter = this.interaction.getActiveInteraction();

          return (
            !!inter &&
            !this.completedAppointmentInteractions[id] && // do not double end
            inter.getId() === id &&
            inter.getOrigin() == ConversationOriginEnum.APPOINTMENT
          );
        })
      )
      .subscribe(async (id) => {
        this.endingInteraction = this.interaction.getActiveInteraction();

        // complete the appointment
        const app = await this.complete(
          this.endingInteraction.getAppointmentId()
        );
        this.setAppointmentAsCompletedInInteraction(id);
        this._appointmentEnded.next(app);
      });

    // terminate conference
    this.interaction.eventClient?.ended$
      .pipe(
        filter((conversationId) => {
          const inter =
            this.endingInteraction || // in genesys we will have it from ending$
            this.interaction.getActiveInteraction(); // in talkdesk we only have it here
          return (
            !this.isInConference && // conference handles for terminate session
            !!inter &&
            !!inter.getRoom() && // if the agent terminates the call, we don't have a room
            inter.getId() === conversationId &&
            inter.getOrigin() == ConversationOriginEnum.APPOINTMENT
          );
        })
      )
      .subscribe(async (_) => {
        const intr =
          this.endingInteraction || this.interaction.getActiveInteraction();

        // join the conference to terminate it
        this.rtcService.client.joinConference({
          id: intr.getRoom(),
          eventHandlers: {
            accepted: (session: IConferenceSession) => {
              setTimeout(() => {
                session.terminate(TerminateReasonEnum.appointmentEnded);
              }, 500);
            },
          },
        });

        this.endingInteraction = undefined;
      });
  }

  private get isInConference() {
    return this.router.url.startsWith("/a?");
  }

  get isAppointmentInteractionAvailable() {
    return (
      this.application.getActiveApplication()?.supportsInteractions() &&
      this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.APPOINTMENT
      ) &&
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED) &&
      this.config.agentParam(AgentParam.SCHEDULE_CHANNEL) ===
        ScheduleTypeEnum.appointment
    );
  }

  public notifyChange(data: IAppointment<IAppointmentRouting>) {
    this._appointmentChanged.next(data);
  }

  private get countParams() {
    const application = this.application.getActiveApplication();
    return {
      applicationId: application.getId(),
      organizationId: stripOrganizationIdPrefix(application.getOrganizationId()),
    };
  }

  /**
   * How many appointments already sit in each time frame of the period starting at
   * `start`. Used to hide timeslots that are already fully booked.
   * @param start beginning of the period
   * @param timeFrame ISO-8601 duration of the period .eg PT24H
   */
  public getAppointmentCounts(
    start: Date,
    timeFrame = "PT24H"
  ): Promise<IAppointmentTimeFrameCount[]> {
    return this.appointmentCountResource
      .get({
        params: {
          ...this.countParams,
          start: start.toISOString(),
          timeFrame,
        },
        urlPostfix: "/count",
      })
      .catch((ex) => {
        debugError("failed to get appointment counts", ex);
        throw ex;
      });
  }

  /**
   * How many appointments already overlap with the given slot.
   * @param start start of the slot
   * @param duration ISO-8601 duration of the slot .eg PT30M
   */
  public getOverlappingAppointmentsCount(
    start: Date,
    duration: string
  ): Promise<{ count: number }> {
    return this.appointmentCountResource
      .get({
        params: {
          ...this.countParams,
          start: start.toISOString(),
          duration,
        },
        urlPostfix: "/overlap/count",
      })
      .catch((ex) => {
        debugError("failed to get overlapping appointments count", ex);
        throw ex;
      });
  }

  public query(
    query: PageableRequest
  ): Promise<IPagedResponse<IAppointment<IAppointmentRouting>>> {
    query.set("applicationId", this.application.getActiveApplication().getId());
    return this.appointmentResource
      .get({
        params: query.getParams(),
        urlPostfix: "/",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public request(
    data: IScheduleAppointmentRequest
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(data, { urlPostfix: "/" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public update(
    data: IScheduleAppointmentOptions
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(data, { urlPostfix: "/" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Updates the state of the specified appointment and sends a related MQTT event
   * @param appointmentId Appointment id
   * @param state starting:CUSTOMER role, participantId required,  inprogress: CUSTOMER & AGENT roles, end: AGENT role
   * @returns 409: Conflict if another participant has already started the conversation and is on queue
   */
  public updateState(
    appointmentId: string,
    state: "starting" | "inprogress" | "end" | "failed",
    options?: {
      participantId?: string;
      systemMessage?: string;
      userMessage?: string;
    }
  ) {
    return this.appointmentResource
      .update(options, {
        urlPostfix: `/${appointmentId}/${state}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public participantFailed(
    appointmentId: string,
    participantId: string,
    options: { systemMessage: string; userMessage: string }
  ): Promise<void> {
    return this.appointmentResource
      .update(options, {
        urlPostfix: `/${appointmentId}/participants/${participantId}/failed`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateRouting(
    appointmentId: string,
    routing: IAppointmentRouting
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        { routing },
        {
          urlPostfix: `/${appointmentId}/interaction`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * accepts incoming interaction as agent
   */
  public acceptInteraction(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          participantId: this.user.getActiveUser().getId(),
        },
        {
          urlPostfix: `/${appointmentId}/accept`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addParticipant(
    appointmentId: string,
    participant: IContactBase
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .create(
        {
          id: appointmentId,
          customer: participant,
        },
        {
          urlPostfix: "/participants/customers/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateParticipant(
    appointmentId: string,
    participantId: string,
    participant: IContactBase
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .update(
        {
          customer: participant,
        },
        {
          urlPostfix: `/${appointmentId}/participants/${participantId}`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateParticipantState(
    appointmentId: string,
    participantState: AppointmentParticipantStateEnum
  ) {
    return this.appointmentResource
      .update(undefined, {
        urlPostfix: `/${appointmentId}/participants/${this.user.appointmentParticipantId}/state`,
        params: { status: participantState },
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getViewState(
    appointment: IAppointment<IAppointmentRouting>
  ): AppointmentViewState {
    const customerState =
      appointment.participants.find((p) => p.type === "CUSTOMER")?.state ||
      "UNSET";

    const agentState =
      appointment.participants.find((p) => p.type === "AGENT")?.state ||
      "UNSET";

    switch (appointment.interaction.state) {
      case "EXPIRED":
        return "EXPIRED";
      case "FAILED":
        return "FAILED";
      case "STARTING":
        return "PENDING";
      case "PENDING":
        // check if end time passed
        return dayjs(appointment.start)
          .add(dayjs.duration(appointment.duration))
          .isBefore(dayjs())
          ? "EXPIRED"
          : "PENDING";

      case "COMPLETE":
        return "COMPLETED";
      case "IN_PROGRESS":
        // end time has passed
        if (
          dayjs().isAfter(
            dayjs(appointment.start).add(dayjs.duration(appointment.duration))
          )
        ) {
          return customerState === "LEFT"
            ? agentState === "UNSET"
              ? "MISSED"
              : "ONGOING"
            : "OVERRUNING";
        } else if (
          // we are between start and end
          dayjs().isBetween(
            dayjs(appointment.start).subtract(1, "minute"),
            dayjs(appointment.start).add(dayjs.duration(appointment.duration))
          )
        ) {
          return "ONGOING";
        } else {
          return "ONGOING";
        }
    }
  }

  async complete(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    try {
      const appointment = await this.get(appointmentId);
      // if appointment is still in progress or pending and the conversation ended
      // update the state
      if (
        ["IN_PROGRESS", "PENDING"].includes(appointment.interaction.state)
        // maybe another one created the appointment and I'm the one that answered it. so don't filter per owner
        // && appointment.participants.find(
        //   (p) => p.id === this.userService.getActiveUser().getId()
        // )
      ) {
        await this.updateState(appointment.id, "end");
        appointment.interaction.state = "COMPLETE";
        return appointment;
      }
    } catch (ex) {
      debugError(ex);
    }
  }

  public remove(id: string): Promise<void> {
    return this.appointmentResource.deleteById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public cancelAppointment(id: string): Promise<void> {
    return this.appointmentResource
      .update({}, { urlPostfix: `/${id}/cancel` })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public get(id: string): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource.getById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public removeParticipant(
    appointmentId: string,
    participantId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .delete({
        urlPostfix: `${appointmentId}/participants/${participantId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getParticipantDetails(
    appointmentId: string,
    participantId: string
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .get({
        urlPostfix: `${appointmentId}/participants/${participantId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addAgentToAppointment(
    id: string,
    userId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(
        {
          id,
          userId,
        },
        {
          urlPostfix: "participants/agents/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addContactToAppointment(
    id: string,
    contact: IAppointmentContact
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(
        {
          id,
          customer: contact,
        },
        {
          urlPostfix: "participants/customers/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public reassignAppointment(
    id: string,
    userId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          id,
          userId,
        },
        {
          urlPostfix: "reassign/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public rescheduleAppointment(
    id: string,
    duration: string,
    start: Date,
    timezone: string,
    notifications: IAppointmentNotification[]
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          id,
          duration,
          start,
          timezone,
          notifications,
        },
        {
          urlPostfix: "reschedule/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public setAppointmentStateEnded(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(null, {
        urlPostfix: `${appointmentId}/end`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public activate(appointmentId: string): Promise<{ url: string }> {
    return this.appointmentResource
      .create(null, {
        urlPostfix: `activate/${appointmentId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   *
   * @param applicationId current application id
   * @param code OTP you got from veriy
   * @returns 204 OK
   */
  public confirmSMSNotification(
    applicationId: string,
    code: string
  ): Promise<void> {
    return this.notificationResource
      .create(
        {
          applicationId,
          code,
        },
        {
          urlPostfix: "sms/confirm",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   *
   * @param applicationId current application id
   * @param code OTP you got from veriy
   * @returns 204 OK
   */
  public confirmEmailNotification(
    applicationId: string,
    code: string
  ): Promise<void> {
    return this.notificationResource
      .create(
        {
          applicationId,
          code,
        },
        {
          urlPostfix: "emails/confirm",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Sends an SMS with an OTP to be confirmed
   *
   * @param applicationId current application id
   * @param fromNumber Provisioned SMS number
   * @param toNumber testing device SMS number
   * @returns an OTP to be provided to "confirm" flow
   */
  public verifySMSNotification(
    applicationId: string,
    fromNumber: string,
    toNumber: string
  ): Promise<{ applicationId: string; validFor: string }> {
    return this.notificationResource
      .create(
        {
          applicationId,
          fromNumber,
          toNumber,
        },
        {
          urlPostfix: "sms/verify",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Sends an email with an OTP to be confirmed
   *
   * @param applicationId current application id
   * @param fromNumber Provisioned SMS number
   * @param toNumber testing device SMS number
   * @returns an OTP to be provided to "confirm" flow
   */
  public verifyEmailNotification(
    applicationId: string,
    domain: string,
    fromName: string,
    toEmail: string
  ): Promise<{ applicationId: string; validFor: string }> {
    return this.notificationResource
      .create(
        {
          applicationId,
          domain,
          fromName,
          toEmail,
        },
        {
          urlPostfix: "emails/verify",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testSMSNotification(
    applicationId: string,
    toNumber: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string
  ) {
    return this.notificationResource
      .create(
        {
          applicationId,
          toNumber,
          locale,
          timezone,
          ...notification,
        },
        {
          urlPostfix: "sms/test",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testWebhookNotification(
    applicationId: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string,
    baseUrl: string,
    subscription?: IWebhookSubscription
  ) {
    const body = {
      applicationId,
      timezone,
      locale,
      type: notification.type,
      period: notification.period,
      properties: notification.properties,
      baseUrl: baseUrl,
      authentication:
        subscription?.authentication ||
        WebhookSubscriptionAuthenticationEnum.NONE,
      oAuth2ClientCredentials: undefined,
      basicAuth: undefined,
    };
    switch (subscription?.authentication) {
      case WebhookSubscriptionAuthenticationEnum.OAUTH2_CLIENT_CREDENTIALS:
        body.oAuth2ClientCredentials = (
          subscription as IWebhookSubscriptionClientCredentials
        ).oAuth2ClientCredentials;
        break;
      case WebhookSubscriptionAuthenticationEnum.HTTP_BASIC:
        body.basicAuth = (
          subscription as IWebhookSubscriptionBasicAuth
        ).basicAuth;
    }

    return this.notificationResource
      .create(body, { urlPostfix: "webhooks/test" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testEmailNotification(
    applicationId: string,
    toEmail: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string
  ) {
    return this.notificationResource
      .create(
        {
          applicationId,
          toEmail,
          locale,
          timezone,
          ...notification,
        },
        {
          urlPostfix: "emails/test",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public async getTagFormats(): Promise<IAppointmentFormat[]> {
    if (this.tagFormats?.length === 0) {
      try {
        const page = await this.notificationResource
          .get({
            urlPostfix: "tags",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.tagFormats = page.content;
      } catch (ex) {}
    }
    return this.tagFormats;
  }

  public async getSupportedLocales(): Promise<IAppointmentLocale[]> {
    if (this.supportedLocales?.length === 0) {
      try {
        const page = await this.notificationResource
          .get({
            urlPostfix: "locales",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.supportedLocales = page.content;
      } catch (ex) {}
    }
    return this.supportedLocales;
  }

  public async getWebhookTypes(): Promise<
    AppointmentWebhookNotificationTypeEnum[]
  > {
    if (this.webhookTypes?.length === 0) {
      try {
        const response = await this.notificationResource
          .get({
            urlPostfix: "webhooks/types",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.webhookTypes = response.types;
      } catch (ex) {}
    }
    return this.webhookTypes;
  }

  public propagateEvent(event: {
    eventId: string;
    type: AppointmentEventType;
    appointmentId: string;
    timestamp: string;
    participantId?: string;
    systemMessage?: string;
    userMessage?: string;
  }) {
    switch (event.type) {
      case "APAgentJoinedEvent":
      case "APAgentLeftEvent":
      case "APCustomerJoinedEvent":
      case "APCustomerLeftEvent":
      case "APCustomerWaitingEvent":
        debug("appointment participant state changed", event);
        this._appointmentParticipantStateChanged.next(event);
        break;

      case "APUpdatedEvent":
        debug("appointment state changed", event);
        this._appointmentStateChanged.next(event);
        this.refreshCachedParticipants(event.appointmentId);
        break;

      case "APRescheduledEvent":
      case "APReassignedEvent":
      case "APInProgressEvent":
      case "APCompletedEvent":
      case "APDeletedEvent":
      case "APExpiredEvent":
      case "APFailedEvent":
      case "APCancelledEvent":
        debug("appointment state changed", event);
        this._appointmentStateChanged.next(event);
        break;
      case "APParticipantFailedEvent":
        debug("appointment participant failed", event);
        this._appointmentParticipantStateChanged.next(event);
        this.notification.warn(
          this.translate.instant("Appointment"),
          { body: this.translate.instant("Customer could not join the appointment") }
        );
        break;
      case "APScheduledEvent":
        debug("appointment created", event);
        this._appointmentCreated.next(event.appointmentId);
        break;
    }
  }

  public isNotificationMutliple(
    type:
      | AppointmentNotificationTypeEnum
      | AppointmentWebhookNotificationTypeEnum
  ) {
    return [
      AppointmentNotificationTypeEnum.room,
      AppointmentNotificationTypeEnum.reminder,
      AppointmentWebhookNotificationTypeEnum.reminder,
    ].includes(type);
  }

  public isNotificationInstant(
    type:
      | AppointmentNotificationTypeEnum
      | AppointmentWebhookNotificationTypeEnum
  ) {
    return [
      AppointmentNotificationTypeEnum.create,
      AppointmentNotificationTypeEnum.update,
      AppointmentNotificationTypeEnum.delete,
      AppointmentNotificationTypeEnum.reschedule,
      AppointmentWebhookNotificationTypeEnum.cancelled,
      AppointmentWebhookNotificationTypeEnum.completed,
      AppointmentWebhookNotificationTypeEnum.created,
      AppointmentWebhookNotificationTypeEnum.inProgress,
      AppointmentWebhookNotificationTypeEnum.interactionUpdated,
      AppointmentWebhookNotificationTypeEnum.noShow,
      AppointmentWebhookNotificationTypeEnum.participantAdded,
      AppointmentWebhookNotificationTypeEnum.participantRemoved,
      AppointmentWebhookNotificationTypeEnum.participantUpdated,
      AppointmentWebhookNotificationTypeEnum.rescheduled,
      AppointmentWebhookNotificationTypeEnum.reassigned,
      AppointmentWebhookNotificationTypeEnum.updated,
    ].includes(type);
  }

  public getNotificationLogs(
    appointmentId: string,
    query: AppointmentLogRequestBuilder
  ): Promise<IPagedResponse<IAppointmentNotificationLog>> {
    return this.appointmentResource
      .get({
        params: query.build().getParams(),
        urlPostfix: appointmentId + "/notifications",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getNotificationLogEvents(
    appointmentId: string,
    query: AppointmentLogEventRequestBuilder
  ): Promise<IPagedResponse<IAppointmentNotificationLogEvent>> {
    return this.appointmentResource
      .get({
        params: query.build().getParams(),
        urlPostfix: appointmentId + "/notifications/events",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public retryNotification(
    appointmentNotificationLogId: string
  ): Promise<IAppointmentNotificationLog> {
    return this.notificationResource
      .update(undefined, {
        urlPostfix: appointmentNotificationLogId + "/retry",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * only ROLE_ADMIN can call it. Trigger cache invalidation to apply changes instantly instead of having to wait up to 30 secs
   */
  public clearCache() {
    if (
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED) &&
      this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.APPOINTMENT
      )
    ) {
      return this.appointmentResource
        .create(null, {
          urlPostfix: `applications/${this.application
            .getActiveApplication()
            .getId()}/invalidate`,
        })
        .catch((ex) => {
          this.errorHandler.handleNotAuthenticatedError(ex);
          throw ex;
        });
    }
  }
}
